import { useState } from 'react'
import { GroupsSection } from '../groups/GroupsSection'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Avatar } from '../chat/Avatar'
import { LastSpeakerFace } from '../sidebar/LastSpeakerFace'
import type { DmPreview } from '../chat/dmUnread'
import { getFriendRequests, getFriends } from '../transport/api'
import { type Verb, runVerb, DONE, errText } from './friendVerbs'
import { Icon } from '../ui/Icon'
import { PixelPet } from '../identity/PixelPet'
import {
  acceptAgentShare,
  declineAgentShare,
  shareErrText,
  type Friend,
  type IncomingAgentShare,
} from '../transport/relay'
import { AGENT_SHARES_KEY, shareLeft, useAgentShares } from './agentShares'
import { Sheet } from '../ui/Sheet'
import { ReportForm, ReportGuidance } from '../shell/ReportSection'
import { BlockDoor } from './Block'
import { InviteSheet } from './InviteSheet'
import { ProfilePageLazy as ProfilePage } from '../auth/ProfilePageLazy'

/**
 * The Group tab's content: your friends and the pending requests. Adding a NEW person
 * moved to the ＋ button's Humans tab — owner, 2026-08-15: 把互加好友放到 + button 裡,
 * Group 只留清單. So this tab is now the roster and the request tray; a friend row opens
 * the DM the tab exists for (onOpenDm). email is the whole identity throughout.
 *
 * The unread marks arrive as a map rather than being fetched here, so the number on
 * the Humans tab and the marks on these rows are the same read of the same rows
 * (chat/dmUnread.ts). A tab that says "2" over a roster with nothing marked is half
 * a mechanism — it tells you the mail exists and then makes you open every friend to
 * find it.
 */
export function FriendsTab({
  selfEmail,
  onOpenDm,
  unreadByEmail,
  previewByEmail,
  onOpenShared,
  openSharedId = null,
  openGroupId = null,
  onGroupOpened,
  onOpenGroupChat,
}: {
  selfEmail: string | null
  /** A friend row opens the user↔user conversation — the tab's whole point. */
  /** `account`: which friend, when the address is two of them (the row's friend-list handle). */
  onOpenDm: (email: string, account?: string) => void
  /**
   * One of a friend's agents opens on the STAGE, the way your own agents do — owner, 2026-10-17:
   * 朋友 Agent 的 session ui 可以用右開的方式 (it was a sheet over this tab).
   */
  onOpenShared?: (shareId: string) => void
  /** The friend's agent on the stage now — its row reads as the open one. */
  openSharedId?: string | null
  /** email → unread in that DM. Absent/0 renders no mark. */
  unreadByEmail?: Map<string, number>
  /** email → the DM's last message, for the card's preview strip (owner,
   *  2026-08-27: 如 Agents 卡片一樣 — same classes, same all-or-nothing rule). */
  previewByEmail?: Map<string, DmPreview>
  /** ＋ → Group just made this one: its sheet opens in the Groups list below the friends. */
  openGroupId?: string | null
  onGroupOpened?: () => void
  /** A group's chat room, by its conversation id — it opens on the stage like a project. */
  onOpenGroupChat?: (convId: string) => void
}) {
  const qc = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)

  const friends = useQuery({ queryKey: ['friends'], queryFn: getFriends })
  const requests = useQuery({ queryKey: ['friend-requests'], queryFn: getFriendRequests })

  /** One mutation for the tray's three verbs — report, then re-derive roster + tray. */
  const action = useMutation({
    mutationFn: ({ email, verb }: { email: string; verb: Verb }) => runVerb(verb, email),
    onSuccess: (_d, { email, verb }) => {
      setNotice(DONE[verb](email))
      void qc.invalidateQueries({ queryKey: ['friends'] })
      void qc.invalidateQueries({ queryKey: ['friend-requests'] })
    },
    onError: (e) => {
      setNotice(errText(e))
      // already_friends / already_requested still mean "re-read the truth".
      void qc.invalidateQueries({ queryKey: ['friends'] })
      void qc.invalidateQueries({ queryKey: ['friend-requests'] })
    },
  })
  const busy = action.isPending
  const fire = (email: string, verb: Verb) => action.mutate({ email, verb })

  /*
   * AGENTS SHARED WITH ME — owner, 2026-10-17: 好友方可以選擇接受分享, 或是拒絕. 接受分享之後, 在 Humans
   * 裡的好友欄位. 可以看到分享 Agent 的好友其分享出來的 Agent 的下拉選單. Offers wait in the tray above
   * the roster; an accepted one hangs off its friend's row and opens the conversation.
   */
  const sharesQ = useAgentShares()
  const incomingShares = (sharesQ.data?.incoming ?? []).filter((s) => s.status !== 'expired')
  const offers = incomingShares.filter((s) => s.status === 'pending')
  const sharedBy = new Map<string, IncomingAgentShare[]>()
  for (const s of incomingShares) {
    if (s.status !== 'accepted' || !s.from.email) continue
    const k = s.from.email.toLowerCase()
    sharedBy.set(k, [...(sharedBy.get(k) ?? []), s])
  }
  const [dropFor, setDropFor] = useState<string | null>(null)
  const [inviting, setInviting] = useState(false)
  /** Whose page is open — owner, 2026-10-20: 好友可以在好友頁面點擊好友的頭像來看到使用者 profile. */
  const [profileOf, setProfileOf] = useState<Friend | null>(null)
  /*
   * REPORT WHERE IT ARRIVES — avg-devapp-appstore, 2026-10-17 (Guideline 1.2): an offer comes
   * from another person as a push, and the screen it lands on must have a door marked Report.
   * The same form as a friend's sheet (More › Friends), about the person who offered.
   */
  const [reportOffer, setReportOffer] = useState<IncomingAgentShare | null>(null)
  const answer = useMutation({
    mutationFn: ({ id, yes }: { id: string; yes: boolean }) => (yes ? acceptAgentShare(id) : declineAgentShare(id)),
    onError: (e) => setNotice(shareErrText(e)),
    onSettled: () => void qc.invalidateQueries({ queryKey: AGENT_SHARES_KEY }),
  })
  const nowS = Date.now() / 1000

  const friendRows = friends.data?.friends ?? []
  const incoming = requests.data?.incoming ?? []
  const outgoing = requests.data?.outgoing ?? []

  return (
    <div className="fr-tab" role="group" aria-label="Friends">
      {notice && (
        <div className="fr-notice" role="status">
          {notice}
        </div>
      )}

      {offers.length > 0 && (
        <div className="fr-tray" data-testid="share-offers">
          <div className="fr-head">Agents shared with you</div>
          {offers.map((o) => (
            <div className="fr-row fr-offer" key={o.share_id}>
              <PixelPet name={o.agent.label} seed={o.agent.hash || o.agent.label} size={24} />
              <span className="fr-rowname">
                {o.agent.label}
                <span className="fr-offer-sub">
                  {o.from.name || o.from.email} · {o.agent.machine || 'their machine'} · {shareLeft(o.expires_at, nowS, 'they')}
                </span>
                {/* Its own line: on a phone the line above is cut short, and took Report with it.
                    Block beside it (owner, 2026-10-17, App Store 1.2): where the unwanted thing arrived. */}
                <span className="fr-offer-acts">
                  <button type="button" className="fr-offer-report" onClick={() => setReportOffer(o)}>
                    Report
                  </button>
                  <BlockDoor
                    name={o.from.name || o.from.email || 'this person'}
                    email={o.from.email ?? ''}
                    account={o.from_account}
                  />
                </span>
              </span>
              <button
                type="button"
                className="fr-btn ok"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: o.share_id, yes: true })}
              >
                Accept
              </button>
              <button
                type="button"
                className="fr-btn"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: o.share_id, yes: false })}
              >
                Decline
              </button>
            </div>
          ))}
        </div>
      )}

      {(incoming.length > 0 || outgoing.length > 0) && (
        <div className="fr-tray">
          {incoming.length > 0 && <div className="fr-head">Requests to you</div>}
          {incoming.map((r) => (
            <div className="fr-row" key={`in:${r.email}`}>
              <Avatar picture={null} label={r.name ?? r.email} email={r.email} className="fr-pic-sm" />
              <span className="fr-rowname">
                {r.name ?? r.email}
                <BlockDoor name={r.name ?? r.email} email={r.email} />
              </span>
              <button type="button" className="fr-btn ok" disabled={busy} onClick={() => fire(r.email, 'accept')}>
                Accept
              </button>
              <button type="button" className="fr-btn" disabled={busy} onClick={() => fire(r.email, 'reject')}>
                Decline
              </button>
            </div>
          ))}
          {outgoing.length > 0 && <div className="fr-head">Sent by you</div>}
          {outgoing.map((r) => (
            <div className="fr-row" key={`out:${r.email}`}>
              <Avatar picture={null} label={r.name ?? r.email} email={r.email} className="fr-pic-sm" />
              <span className="fr-rowname">{r.name ?? r.email}</span>
              <span className="fr-pending">Requested</span>
              <button type="button" className="fr-btn" disabled={busy} onClick={() => fire(r.email, 'cancel')}>
                Cancel
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="fr-list">
        {/* Invite, at the right of the heading — owner, 2026-10-19: friends 的右側放置一個 Invite 的按鈕. */}
        <div className="fr-head-row">
          <div className="fr-head">Friends{friendRows.length ? ` · ${friendRows.length}` : ''}</div>
          <button type="button" className="fr-invite" onClick={() => setInviting(true)} aria-haspopup="dialog">
            <Icon name="person_add" size={15} />
            Invite
          </button>
        </div>
        {friends.isPending && <div className="fr-empty">loading…</div>}
        {friends.isError && <div className="fr-empty fr-err">couldn’t load friends</div>}
        {!friends.isPending && !friends.isError && friendRows.length === 0 && (
          <div className="fr-empty">No friends yet — add someone from the ＋ button.</div>
        )}
        {(() => {
          /* The strip is all-or-nothing across the roster — the project grid's own
             rule, for the same reason: a list where some cards have a second block
             and some don't reads as broken rather than as quiet. */
          const anyLast = friendRows.some((f) => !!previewByEmail?.get(f.email)?.text)
          return friendRows.map((f) => {
            const pv = previewByEmail?.get(f.email)
            const theirs = sharedBy.get(f.email.toLowerCase()) ?? []
            const open = dropFor === (f.account ?? f.email)
            return (
          <div className="fr-friend-wrap" key={f.account ?? f.email}>
          <div className="fr-friend-line">
          {/* THE FACE OPENS THEIR PAGE, the rest of the row their conversation — owner, 2026-10-20:
              好友可以在好友頁面點擊好友的頭像來看到使用者 profile. Two buttons side by side, never one inside
              the other: a button in a button is no button to a screen reader. */}
          <button
            type="button"
            className="fr-face"
            onClick={() => setProfileOf(f)}
            aria-label={`${f.name ?? f.email}'s profile`}
            title={`${f.name ?? f.email}'s profile`}
            aria-haspopup="dialog"
          >
            <Avatar
              picture={f.picture ?? null}
              label={f.name ?? f.email}
              email={f.email}
              className="fr-pic-sm"
            />
          </button>
          <button
            type="button"
            className="fr-friend"
            onClick={() => onOpenDm(f.email, f.account)}
            aria-label={`Message ${f.name ?? f.email}`}
            title={`Message ${f.name ?? f.email}`}
          >
            {/* The email line is GONE (owner, 2026-08-27: 把朋友的 email 隱藏, preview
                並排到名稱下方 — the chat-list shape). The name keeps the email as its
                fallback for a friend with no profile name, and the row's title still
                carries it for anyone who needs the exact address. The preview sits
                where the email used to: under the name, same column — the project
                cards' drawing (face/dot + clamped text) in compact form. */}
            <span className="fr-rowname">
              {f.name ?? f.email}
              {anyLast && (
                <span className="sb-plast fr-plast">
                  <LastSpeakerFace
                    hash={pv?.fromHash ?? ''}
                    who={pv?.from ?? ''}
                    email={pv?.fromEmail ?? ''}
                    picture={pv?.fromPicture ?? ''}
                    size={14}
                  />
                  <span className="sb-plast-txt">{pv?.text || '\u00a0'}</span>
                </span>
              )}
            </span>
            {(() => {
              /* Same cap and the same reason as the rail's rows: past nine the exact
                 number stops changing what anyone does, and "99+" would eclipse the
                 name it sits beside. The title keeps the real count. */
              const n = unreadByEmail?.get(f.email) ?? 0
              if (n <= 0) return null
              return (
                <span
                  className="fr-unread"
                  title={`${n} unread message${n === 1 ? '' : 's'}`}
                >
                  {n > 9 ? '9+' : n}
                </span>
              )
            })()}
            {f.email === selfEmail && <span className="fr-pending">you</span>}
          </button>
          {theirs.length > 0 && (
            <button
              type="button"
              className="fr-agents-chip"
              aria-expanded={open}
              aria-label={`Agents ${f.name ?? f.email} shared with you`}
              onClick={() => setDropFor(open ? null : (f.account ?? f.email))}
            >
              <Icon name="smart_toy" size={14} />
              {theirs.length}
              <Icon name={open ? 'expand_less' : 'expand_more'} size={14} />
            </button>
          )}
          </div>
          {open && theirs.length > 0 && (
            <div className="fr-agents">
              {theirs.map((s) => (
                <button
                  type="button"
                  className={`fr-agent${openSharedId === s.share_id ? ' on' : ''}`}
                  key={s.share_id}
                  aria-current={openSharedId === s.share_id ? 'page' : undefined}
                  onClick={() => {
                    setDropFor(null)
                    onOpenShared?.(s.share_id)
                  }}
                >
                  <PixelPet name={s.agent.label} seed={s.agent.hash || s.agent.label} size={22} />
                  <span className="fr-agent-name">{s.agent.label}</span>
                  <span className="fr-agent-where">
                    {s.agent.machine} · {shareLeft(s.expires_at, nowS, 'they')}
                  </span>
                </button>
              ))}
            </div>
          )}
          </div>
            )
          })
        })()}
      </div>
      {/* Groups, under the friends — owner, 2026-10-18: group 的欄位可以放在 SidePanel Humans 的那一個頁面裡面. */}
      <GroupsSection
        openGid={openGroupId}
        {...(onGroupOpened ? { onOpened: onGroupOpened } : {})}
        onOpenDm={onOpenDm}
        {...(onOpenGroupChat ? { onOpenChat: onOpenGroupChat } : {})}
      />
      {inviting && <InviteSheet onClose={() => setInviting(false)} />}
      {profileOf && (
        <ProfilePage
          key={`${profileOf.email}:${profileOf.account ?? ''}`}
          who={{ email: profileOf.email, ...(profileOf.account ? { account: profileOf.account } : {}) }}
          onClose={() => setProfileOf(null)}
          onMessage={(email) => {
            setProfileOf(null)
            onOpenDm(email, profileOf.account)
          }}
        />
      )}
      {reportOffer && (
        <Sheet title={`Report ${reportOffer.from.name || reportOffer.from.email || 'this person'}`} onClose={() => setReportOffer(null)}>
          <ReportGuidance />
          <ReportForm
            about={`friend:${reportOffer.from.email ?? ''}`}
            aboutLabel={reportOffer.from.name || reportOffer.from.email || ''}
          />
        </Sheet>
      )}
    </div>
  )
}
